import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useT } from "../lib/i18n";
import { Cover } from "./MixCard";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const Result = ({ m, i, list }) => {
  const { playMix, mix: current, playing } = usePlayer();
  const active = current?.id === m.id && playing;
  return (
    <li className="flex items-center gap-3 py-3 border-b border-white/5">
      <button data-testid={`search-result-play-${i}`} onClick={() => playMix(m, list)} aria-label={active ? "Pause" : "Play"}
        className="relative w-14 h-14 shrink-0 overflow-hidden group">
        <Cover mix={m} />
        <span className="absolute inset-0 grid place-items-center bg-black/50 text-[#1FD1FF] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {active ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
        </span>
      </button>
      <Link to={`/mix/${m.id}`} data-testid={`search-result-${i}`} className="min-w-0 flex-1 hover:text-[#1FD1FF] transition-colors">
        <span className="block font-semibold truncate">{m.title}</span>
        <span className="block font-mono text-[11px] text-[#A8B3C4] truncate">{m.genre} · {m.bpm || "—"} BPM{m.key ? ` · ${m.key}` : ""}</span>
      </Link>
    </li>
  );
};

export const SearchOverlay = ({ open, onClose }) => {
  const { t } = useT();
  const { withOverrides } = usePlayer();
  const loc = useLocation();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [genres, setGenres] = useState([]);
  const [done, setDone] = useState(false);

  useEffect(() => { onClose(); }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => inputRef.current?.focus(), 80);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    if (!genres.length) api.get("/genres").then((r) => setGenres(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    return () => { clearTimeout(id); window.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const term = q.trim();
    if (!term) { setRes([]); setDone(false); return undefined; }
    const id = setTimeout(() => {
      api.get("/mixes", { params: { q: term } }).then((r) => { setRes(Array.isArray(r.data) ? r.data.slice(0, 12) : []); setDone(true); })
        .catch(() => { setRes([]); setDone(true); });
    }, 220);
    return () => clearTimeout(id);
  }, [q]);

  const list = res.map(withOverrides);
  const gMatch = q.trim() ? genres.filter((g) => norm(g.name).includes(norm(q.trim()))) : genres.slice(0, 10);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="search-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[70] bg-[#04070D] overflow-y-auto" data-lenis-prevent>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
            <div className="h-16 flex items-center gap-3 border-b border-white/15">
              <Search className="w-5 h-5 text-[#1FD1FF] shrink-0" />
              <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")}
                className="flex-1 min-w-0 bg-transparent text-lg sm:text-xl focus:outline-none placeholder:text-[#A8B3C4]" enterKeyHint="search" />
              <button data-testid="search-close-button" onClick={onClose} aria-label={t("menu.close")} className="p-2 -mr-2 text-white hover:text-[#1FD1FF]"><X className="w-6 h-6" /></button>
            </div>
            {gMatch.length > 0 && (
              <div className="mt-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1FD1FF] mb-3">{t("menu.genres")}</p>
                <div className="flex flex-wrap gap-2">
                  {gMatch.map((g) => (
                    <Link key={g.name} to={`/generos/${encodeURIComponent(g.name)}`} data-testid={`search-genre-${norm(g.name).replace(/[^a-z0-9]+/g, "-")}`}
                      className="px-3 py-1.5 font-mono text-xs uppercase tracking-widest border border-white/15 text-white hover:bg-[#1FD1FF] hover:text-black hover:border-[#1FD1FF] transition-colors">{g.name}</Link>
                  ))}
                </div>
              </div>
            )}
            {q.trim() && (
              <div className="mt-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#1FD1FF] mb-1">{t("nav.mixes")}</p>
                {done && list.length === 0
                  ? <p data-testid="search-empty" className="py-6 text-[#A8B3C4]">{t("search.empty")}</p>
                  : <ul data-testid="search-results">{list.map((m, i) => <Result key={m.id} m={m} i={i + 1} list={list} />)}</ul>}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};
